import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../modules/layout/components/ToastContext';
import { post } from '../_request';
import { useNavigate } from 'react-router-dom';

export interface CreateUserDto {
  Name: string;
  Username: string;
  Email: string;
  MobileNumber?: string;
  PPID: string;
  Description?: string;
  UserType: string;
  ProfileState: string;
  LanguageCode: string;
  Category?: string;
  IdpRealm?: string;
}

interface CreateUserProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function CreateUser({onSuccess, onCancel}: CreateUserProps) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);

    const [requiredErrors, setRequiredErrors] = useState<Record<string, string >>({});
    const requiredFields = ['Username', 'Email',  'PPID'];
    const createUser = async (dto: CreateUserDto) => {
      setRequiredErrors({});
      setLoading(true);
      try {
        const result: any = await post('/api/UserEvents/createagentuser', dto,false,true);
        if (result?.data?.IsSuccess || result?.IsSuccess) {
          showToast(t('UserCreatedSuccessfully'), 'success');
          onSuccess?.();
        }
      } catch (err: any) {
          const status = err?.response?.status;
          const rawData = err?.response?.data;
          let messageText = rawData?.Message || rawData?.message || "";
          try {
            if (typeof messageText === 'string' && messageText.startsWith('{')) {
              const parsed = JSON.parse(messageText);
              messageText = parsed.Message || parsed.message || messageText;
            }
          } catch (e) {
            console.error("Failed to parse internal error JSON", e);
          }
          if (status === 401) {
            navigate("/auth/login");
            return;
          } 
          if (status === 403) {
            navigate("/error/403");
            return;
          }
          if (status === 500) {
            navigate("/error/500");
            return;
          }
          if (status === 409) {
          const serverErrors: Record<string, string> = {};
          const lowerMsg = (messageText || "").toLowerCase();

          if (lowerMsg.includes('email')) {
            serverErrors.Email = t('EmailAlreadyInUse');
          }

          if (lowerMsg.includes('username')) {
            serverErrors.Username = t('UsernameAlreadyInUse');
          }

           if (lowerMsg.includes('mobile number')) {
              serverErrors.MobileNumber = t('MobileNumberAlreadyInUse');
            }

          if (Object.keys(serverErrors).length > 0) {
            setRequiredErrors(prev => ({
              ...prev,
              ...serverErrors,
            }));
            return;
          }

          showToast(messageText || t('ConflictError'), 'error');
          return;
        }
        showToast(t('ConnectionError'), 'error');
      } finally {
        setLoading(false);
      }
    };

    const clean = (v: FormDataEntryValue | null) =>
      v && String(v).trim() !== '' ? String(v) : undefined;

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);
      const errors: Record<string, string> = {};

      const dto: CreateUserDto = {
        Username: clean(formData.get(('Username')))!,
        Name: clean(formData.get('Name'))!,
        MobileNumber: clean(formData.get('MobileNumber'))!,
        Email: clean(formData.get('Email'))!,
        PPID: clean(formData.get('PPID'))!,
        UserType: clean(formData.get('UserType'))!,
        ProfileState: clean(formData.get('ProfileState'))!,
        LanguageCode: clean(formData.get('LanguageCode'))!,
        Description: clean(formData.get('Description')),
        Category: 'Agent'
      };
  
      requiredFields.forEach(field => {
        if (!dto[field as keyof CreateUserDto]) {
          errors[field] = t('FieldIsRequired');
        }
      });

      if (dto.Email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dto.Email)) {
        errors['Email'] = t('InvalidEmailFormat');
      }

      setRequiredErrors(errors);

      if (Object.keys(errors).length === 0) {
        createUser(dto);
      }
    };

    const fields = [
      { name: 'Name' },
      { name: 'Email', required: true },
      { name: 'Username', required: true },   
      { name: 'MobileNumber'},
      { name: 'Description' },
      { name: 'PPID', required: true},         
      { name: 'LanguageCode', type: 'select' },
      { name: 'UserType', type: 'select' }
    ];
    const dropdownOptions: Record<string, { value: string; label: string }[]> = {

      UserType: [
        { value: 'Agent', label: t('Agent') },
        { value: 'Spoke', label: t('Spoke') },
        { value: 'Representative', label: t('Representative') },
        { value: 'Delegate', label: t('Delegate') },
        { value: 'Unknown', label: t('Unknown') },
      ],

      LanguageCode: [
        { value: 'sq', label: 'Shqip (Albanian)' },
        { value: 'en', label: 'English' }
      ] 
    };
   
    return (
      <div className="gg-container-creation-form">
        <div className="gg-card-creation-form mt-5 ms-3">
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              {fields.map(({ name, type }) => {
                const isRequired = requiredFields.includes(name);
                const options = dropdownOptions[name];
                const isSelect = !!options;

                return (
                  
                  <div key={name} className="form-group">
                    <label htmlFor={name} className="text-sm font-semibold text-gray-700 ">
                      {t(name)}
                      {isRequired && <span className="required-input">*</span>}
                    </label>

                    <div className="input-wrapper">
                      {isSelect ? (
                        <select
                          id={name}
                          name={name}
                          defaultValue=""
                          className={`form-control ${requiredErrors[name] ? 'error' : ''}`}
                        >
                          <option value="" disabled>{t('SelectOption')}</option>
                          {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          id={name}
                          name={name}
                          type={type ?? 'text'}
                          onChange={() => {
                            if (requiredErrors[name]) {
                              setRequiredErrors(prev => {
                                const { [name]: removed, ...rest } = prev;
                                return rest;
                              });
                            }
                          }}
                          className={`form-control ${requiredErrors[name] ? 'error' : ''}`}
                        />                       
                      )}
                       {requiredErrors[name] && (
                        <span className="error-text">
                          {requiredErrors[name]}
                        </span>
                      )}
                    </div>
                </div>                        
                );
              })}
            </div>
            <div className="mt-3 w-full d-flex justify-content-end gap-3">
              <button type="button" className="btn btn-secondary px-6 py-2" onClick={onCancel}>
                {t('Cancel')}
              </button>
              <button type="submit" disabled={loading} className="btn gg-btn gg-btn-submit px-8 py-2">
                {loading ? t('Creating...') : t('CreateUser')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
}
